import { HttpParams } from '@angular/common/http';
import {
  Component,
  Injector,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { MenuInfo } from '@src/app/core/models/menu-info';
import { ExtranetAuthenticationService } from '@src/app/core/services/extranet-authentication.service';
import { ExtranetMenuService } from '@src/app/core/services/extranet-menu.service';
import { MenuProcessorService } from '@src/app/core/services/menu-processor.service';
import { PasswordService } from '@src/app/core/services/password.service';
import { SelectItem } from 'primeng/api';
import {
  AppConfig,
  BaseComponent,
  StorageService,
  SystemDialogService,
  UserProfileService,
} from 'sigv-library';
import { UserProfile } from 'sigv-library/model';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_KEY,
  SYSTEM_CODE,
} from 'src/app/core/constants/global-constants.constant';
import {
  GlobalJobSchedulerService,
  RepeatableJob,
} from 'src/app/core/services/global-job-scheduler.service';
import { SystemEnvService } from 'src/app/core/services/system-env.service';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';

@Component({
  selector: 'sigv-extranet-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  isMenuVisible$ = this.extranetMenuService.getSideMenuObservable();

  isWindowOpen: boolean = window.location.search.includes('isWindowOpen=true');
  
  currentMenuItem = '';
  locale = DEFAULT_LANGUAGE;
  languageOptions: SelectItem[] = [];
  displayMenus: any[] = [];
  userProfile: UserProfile | null = null;

  private updateTodoJob: RepeatableJob = {
    jobName: 'Refresh Todo Count Job',
    jobAction: () => {
      this.extranetMenuService.updateDisplayMenu();
    },
  };

  constructor(
    injector: Injector,
    private router: Router,
    private extranetMenuService: ExtranetMenuService,
    private authenticationService: ExtranetAuthenticationService,
    private userProfileService: UserProfileService,
    private storageService: StorageService,
    private systemDialogService: SystemDialogService,
    private todoMenuDataService: MenuProcessorService,
    private globalJobScheculer: GlobalJobSchedulerService,
    private systemEnvService: SystemEnvService,
    private passwordService: PasswordService
  ) {
    super(injector);
    const languageList: any[] = this.envConfig.languageList ?? [];
    this.languageOptions = (
      languageList.length === 0
        ? [{ name: 'English', value: DEFAULT_LANGUAGE }]
        : languageList
    ).map((x) => ({ label: x.name, value: x.value }));

    this.extranetMenuService.menuItems$.subscribe((results) => {
      this.displayMenus = results;
    });

    this.globalJobScheculer.addJob(this.updateTodoJob);
  }

  get envConfig(): any {
    return AppConfig.envConfig;
  }

  get userInfoDisplay(): string {
    return `${this.userProfileService.currentUserValue?.userNo ?? ''} ${
      this.userProfileService.currentUserValue?.userName ?? ''
    }`;
  }

  ngOnInit(): void {
    const localeVal = this.storageService.local.getItem(LANGUAGE_KEY);
    this.locale =
      this.languageOptions.find((x) => x.value === localeVal)?.value ??
      DEFAULT_LANGUAGE;
    this.storageService.local.setItem(LANGUAGE_KEY, this.locale);
    this.translateService.use(this.locale);

    this.userProfile = this.userProfileService.currentUserValue;
    this.userProfileService.currentUserSubject.subscribe({
      next: (profile) => {
        this.userProfile = profile;
      },
    });
  }

  ngOnDestroy(): void {
    this.globalJobScheculer.removeJob(this.updateTodoJob);
  }

  toggleSideMenu(): void {
    this.extranetMenuService.toggleSideMenu();
  }

  logout(): void {
    this.router.navigate(['/logout']);
  }

  onLanguageChange(lang: string): void {
    this.locale = lang;
    this.translateService.use(this.locale);
    this.storageService.local.setItem(LANGUAGE_KEY, lang);
    this.onMobileSubMenuClose();
  }

  onMobileMenuItemClick(menu: MenuInfo): void {
    if (menu.items && menu.items.length > 0) {
      this.onMobileSubMenuOpen(menu.srcLabel!);
    } else {
      this.onMobileNavLinkClick(menu);
    }
  }

  onMobileSubMenuOpen(targetSubMenu: string): void {
    if (targetSubMenu) {
      this.currentMenuItem = targetSubMenu;
    }
  }

  onNavLinkClick(menu: MenuInfo): void {
    this.logger.debug(menu);
    if (menu.data?.functionCode?.toLowerCase()?.startsWith('extodo')) {
      return;
    }
    const targetSystemInfo = this.systemEnvService.systemList.find(
      (x) => x.systemCode === menu.data?.systemCode
    );
    if (!targetSystemInfo) {
      throw 'Url (systemHostUrl) is not configured in RBAC. Please contact IT.';
    }
    if (isNullOrEmpty(menu.entryUrl)) {
      throw 'Url (entryUrl) is not configured in RBAC. Please contact IT.';
    }
    if (menu.data!.systemCode === SYSTEM_CODE) {
      this.router.navigateByUrl(menu.entryUrl!);
    } else {
      this.crossDomainRedirection(targetSystemInfo.systemHostUrl!, menu);
    }
  }

  onMobileSubMenuClose(menu?: MenuInfo): void {
    this.currentMenuItem = '';
    // 檢查沒有子項目的才可以關掉side menu
    if (menu) {
      if (!menu.items) {
        this.extranetMenuService.toggleSideMenu();
      }
    } else {
      this.extranetMenuService.toggleSideMenu();
    }
  }

  onMobileNavLinkClick(menu: MenuInfo): void {
    this.logger.debug(menu);
    this.onNavLinkClick(menu);
    this.onMobileSubMenuClose();
  }

  onPasswordChange(isOK: boolean): void {
    if (isOK) {
      this.toastService.success(
        'Password Reset Completed, Please login again.'
      );
      this.onMobileSubMenuClose();
      this.router.navigate(['/']);
    } else {
      this.systemDialogService.showErrorDialog([
        {
          description: 'Password is Fail to change',
        },
      ]);
    }
  }

  private crossDomainRedirection(hostUrl: string, menu: MenuInfo): void {
    this.authenticationService
      .crossDomainLogin(this.authenticationService.idToken)
      .subscribe({
        next: (sessionId: any) => {
          const timestamp = new Date().getTime() / 1000;
          const secretStr = JSON.stringify({ key: sessionId, timestamp });
          const encryptedSecret =
            this.passwordService.getEncryptedData(secretStr);
          const dataStr = JSON.stringify(menu.data);
          const params = new HttpParams()
            .append(
              'email',
              encodeURIComponent(
                this.userProfileService.currentUserValue!.email!
              )
            )
            .append('secret', encodeURIComponent(encryptedSecret))
            .append('redirectTo', encodeURIComponent(menu.entryUrl ?? ''))
            .append('data', encodeURIComponent(dataStr));
          const url = `${hostUrl}redirection?${params.toString()}`;
          window.location.href = url;
        },
      });
  }
}
